import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import {
  Globe, Plug, EnvelopeSimple, FileText, MagnifyingGlass, Database,
  CircleNotch, CaretRight, Shield, Lightning, CheckCircle, Cpu,
  ArrowLeft, Lock, Gear, Eye, Storefront, Plus, Package, Download,
  Power, Trash, CaretDown, Funnel, X, ArrowSquareOut, PuzzlePiece,
  Check, Warning, Star, Code, Robot, Link as LinkIcon
} from '@phosphor-icons/react';
import AddToAgentModal from '../components/AddToAgentModal';

const ICON_MAP = {
  globe: Globe, plug: Plug, mail: EnvelopeSimple, 'file-text': FileText,
  search: MagnifyingGlass, database: Database,
};

const TRUST_COLORS = {
  native_managed: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Native Managed' },
  verified_partner: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Verified Partner' },
  workspace_private: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Workspace Private' },
  external_registered: { bg: 'bg-orange-500/10', text: 'text-orange-400', label: 'External' },
};

const SECTION_BADGES = {
  native: { label: 'Native', className: 'bg-emerald-500/10 text-emerald-400' },
  partner: { label: 'Partner', className: 'bg-blue-500/10 text-blue-400' },
};

// ─── App Card ────────────────────────────────────────────────────
const AppCard = ({ app, installed, onView, onInstall, onAddToAgent }) => {
  const Icon = ICON_MAP[app.icon] || (app.store_section === 'partner' ? PuzzlePiece : Cpu);
  const section = SECTION_BADGES[app.store_section] || SECTION_BADGES.native;
  const trust = TRUST_COLORS[app.trust_class] || TRUST_COLORS.native_managed;

  return (
    <div
      data-testid={`app-card-${app.app_id || app.submission_id}`}
      className="group bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl p-5 cursor-pointer hover:border-[var(--brand-primary)]/40 hover:shadow-lg hover:shadow-[var(--brand-primary)]/5 transition-all"
      onClick={() => onView(app)}
    >
      <div className="flex items-start gap-4">
        {app.icon_url ? (
          <img src={app.icon_url} alt="" className="w-11 h-11 rounded-xl object-cover flex-shrink-0" />
        ) : (
          <div className="w-11 h-11 rounded-xl bg-[var(--brand-primary)]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[var(--brand-primary)]/20 transition-colors">
            <Icon size={22} weight="duotone" className="text-[var(--brand-primary)]" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] truncate">{app.name}</h3>
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium uppercase ${section.className}`}>{section.label}</span>
            {installed && <Check size={12} weight="bold" className="text-emerald-400" />}
          </div>
          <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2">{app.short_description || app.description}</p>
          <div className="flex items-center gap-2 mt-2.5">
            <span className="text-[10px] text-[var(--text-muted)] flex items-center gap-1">
              <Lightning size={10} weight="bold" />{app.capabilities?.length || 0} capabilities
            </span>
            <span className="text-[var(--text-muted)]">&middot;</span>
            <span className={`text-[10px] flex items-center gap-1 ${trust.text}`}>
              <Shield size={10} weight="fill" />{trust.label}
            </span>
            {app.developer_name && (
              <>
                <span className="text-[var(--text-muted)]">&middot;</span>
                <span className="text-[10px] text-[var(--text-muted)]">{app.developer_name}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
          {installed && (
            <button
              onClick={(e) => { e.stopPropagation(); onAddToAgent?.(app); }}
              className="px-2 py-1 rounded-md bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] text-[10px] font-medium
                hover:bg-[var(--brand-primary)]/20 opacity-0 group-hover:opacity-100 transition-all flex items-center gap-1"
              data-testid={`add-to-agent-${app.app_id || app.submission_id}`}
              title="Add to Agent"
            >
              <Robot size={10} />Add to Agent
            </button>
          )}
          <CaretRight size={16} className="text-[var(--text-muted)] group-hover:text-[var(--brand-primary)] transition-colors mt-1" />
        </div>
      </div>
    </div>
  );
};

// ─── App Detail Modal ─────────────────────────────────────────────
const AppDetailModal = ({ app, installation, onClose, onInstall, onUninstall, onToggle, onAddToAgent }) => {
  const Icon = ICON_MAP[app.icon] || (app.store_section === 'partner' ? PuzzlePiece : Cpu);
  const section = SECTION_BADGES[app.store_section] || SECTION_BADGES.native;
  const trust = TRUST_COLORS[app.trust_class] || TRUST_COLORS.native_managed;
  const [detailData, setDetailData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const appId = app.app_id || app.submission_id;
        const [detailRes, analyticsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/app-store/apps/${appId}`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/app-store/analytics/app/${appId}`, { credentials: 'include' }).catch(() => null),
        ]);
        if (detailRes.ok) setDetailData(await detailRes.json());
        if (analyticsRes?.ok) setAnalytics(await analyticsRes.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchDetail();
  }, [app]);

  const detail = detailData || app;
  const isInstalled = installation || detail.installed;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="app-detail-modal">
        {loading && !detailData ? (
          <div className="flex justify-center py-16"><CircleNotch size={28} className="animate-spin text-[var(--brand-primary)]" /></div>
        ) : (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors" data-testid="close-app-detail"><ArrowLeft size={18} /></button>
                <span className="text-xs text-[var(--text-muted)]">App Store</span>
              </div>
              {!isInstalled && detail.store_section !== 'native' && (
                <button
                  onClick={() => onInstall(detail)}
                  className="px-4 py-2 bg-[var(--brand-primary)] text-white rounded-lg text-sm font-medium hover:bg-[var(--brand-primary-hover)] transition-colors flex items-center gap-2"
                  data-testid="install-app-btn"
                >
                  <Download size={16} />Install
                </button>
              )}
              {isInstalled && detail.store_section !== 'native' && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onAddToAgent?.(detail)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium border border-[var(--brand-primary)]/30 text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10 transition-colors flex items-center gap-1.5"
                    data-testid="detail-add-to-agent-btn"
                  >
                    <Robot size={14} />Add to Agent
                  </button>
                  <button
                    onClick={() => onToggle(detail, !(installation?.enabled ?? true))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${installation?.enabled !== false ? 'border-amber-500/30 text-amber-400 hover:bg-amber-500/10' : 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10'}`}
                    data-testid="toggle-app-btn"
                  >
                    <Power size={14} className="inline mr-1" />{installation?.enabled !== false ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={() => onUninstall(detail)}
                    className="px-3 py-1.5 border border-red-500/30 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/10 transition-colors"
                    data-testid="uninstall-app-btn"
                  >
                    <Trash size={14} className="inline mr-1" />Uninstall
                  </button>
                </div>
              )}
              {/* Native apps always show Add to Agent (they're always available) */}
              {detail.store_section === 'native' && (
                <button
                  onClick={() => onAddToAgent?.(detail)}
                  className="px-4 py-2 bg-[var(--brand-primary)] text-white rounded-lg text-sm font-medium hover:bg-[var(--brand-primary-hover)] transition-colors flex items-center gap-2"
                  data-testid="detail-add-to-agent-btn"
                >
                  <Robot size={16} />Add to Agent
                </button>
              )}
            </div>

            {/* Header */}
            <div className="flex items-start gap-4 mb-6">
              {detail.icon_url ? (
                <img src={detail.icon_url} alt="" className="w-14 h-14 rounded-2xl object-cover flex-shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded-2xl bg-[var(--brand-primary)]/10 flex items-center justify-center flex-shrink-0">
                  <Icon size={28} weight="duotone" className="text-[var(--brand-primary)]" />
                </div>
              )}
              <div>
                <h2 className="text-lg font-bold text-[var(--text-primary)]">{detail.name}</h2>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${section.className}`}>{section.label} App</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${trust.bg} ${trust.text}`}>
                    <Shield size={10} weight="fill" className="inline mr-0.5" />{trust.label}
                  </span>
                  {isInstalled && (
                    <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 rounded-full text-[10px] font-medium">
                      <Check size={10} weight="bold" className="inline mr-0.5" />Installed
                    </span>
                  )}
                </div>
              </div>
            </div>

            <p className="text-sm text-[var(--text-secondary)] mb-6 leading-relaxed">{detail.description}</p>

            {/* Metadata Grid */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="p-3 bg-[var(--surface-2)] rounded-xl">
                <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">Auth Model</p>
                <p className="text-sm text-[var(--text-primary)] flex items-center gap-1">
                  <Lock size={12} />{(detail.auth_model || detail.metadata?.auth_model || 'platform_managed')?.replace(/_/g, ' ')}
                </p>
              </div>
              <div className="p-3 bg-[var(--surface-2)] rounded-xl">
                <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">Developer</p>
                <p className="text-sm text-[var(--text-primary)]">{detail.developer_name || detail.company_name || 'Man & Machine'}</p>
              </div>
              <div className="p-3 bg-[var(--surface-2)] rounded-xl">
                <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">Category</p>
                <p className="text-sm text-[var(--text-primary)]">{detail.category || detail.app_type || 'General'}</p>
              </div>
              <div className="p-3 bg-[var(--surface-2)] rounded-xl">
                <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">Provider Type</p>
                <p className="text-sm text-[var(--text-primary)]">{detail.store_section === 'native' ? 'Native' : 'Partner API'}</p>
              </div>
            </div>

            {/* Capabilities */}
            <div className="mb-6">
              <p className="text-xs font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-1.5"><Lightning size={14} weight="bold" />Capabilities</p>
              <div className="space-y-2">
                {(detail.capability_details || []).map(cap => (
                  <div key={cap.id} className="flex items-start gap-3 p-2.5 bg-[var(--surface-2)] rounded-lg">
                    <CheckCircle size={16} weight="fill" className="text-[var(--brand-primary)] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-[var(--text-primary)]">{cap.name || cap.id?.replace(/_/g, ' ')}</p>
                      {cap.description && <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{cap.description}</p>}
                    </div>
                  </div>
                ))}
                {(!detail.capability_details || detail.capability_details.length === 0) && (detail.capabilities || []).map(cap => (
                  <span key={cap} className="inline-block px-2.5 py-1 bg-[var(--brand-primary)]/8 text-[var(--brand-primary)] rounded-lg text-xs font-medium border border-[var(--brand-primary)]/15 mr-2 mb-1">
                    {cap.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>

            {/* Permissions */}
            {detail.permissions?.length > 0 && (
              <div className="mb-6">
                <p className="text-xs font-semibold text-[var(--text-primary)] mb-2 flex items-center gap-1.5"><Lock size={14} />Permissions / Scopes</p>
                <div className="flex flex-wrap gap-1.5">
                  {detail.permissions.map(p => <span key={p} className="px-2 py-0.5 bg-amber-500/8 text-amber-400 border border-amber-500/15 rounded text-[10px]">{p}</span>)}
                </div>
              </div>
            )}

            {/* Setup / Integration */}
            {detail.provider_config && Object.keys(detail.provider_config).length > 0 && (
              <div className="mb-6">
                <p className="text-xs font-semibold text-[var(--text-primary)] mb-2 flex items-center gap-1.5"><Gear size={14} />Integration Config</p>
                <div className="p-3 bg-[var(--surface-2)] rounded-lg">
                  {Object.entries(detail.provider_config).map(([key, val]) => (
                    <div key={key} className="flex items-center justify-between py-1">
                      <span className="text-xs text-[var(--text-secondary)]">{key.replace(/_/g, ' ')}</span>
                      <span className="text-xs text-[var(--text-muted)] font-mono">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Links */}
            {(detail.docs_url || detail.support_url) && (
              <div className="mb-6 flex gap-3">
                {detail.docs_url && (
                  <a href={detail.docs_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--brand-primary)] flex items-center gap-1 hover:underline">
                    <ArrowSquareOut size={12} />Documentation
                  </a>
                )}
                {detail.support_url && (
                  <a href={detail.support_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--brand-primary)] flex items-center gap-1 hover:underline">
                    <ArrowSquareOut size={12} />Support
                  </a>
                )}
              </div>
            )}

            {/* Usage Analytics */}
            {analytics && (analytics.invocation_count > 0 || analytics.install_count > 0) && (
              <div className="mb-6" data-testid="app-detail-analytics">
                <p className="text-xs font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-1.5"><Eye size={14} />Usage Analytics</p>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { label: 'Installs', value: analytics.active_install_count, color: 'text-blue-400' },
                    { label: 'Workspaces', value: analytics.workspace_count, color: 'text-purple-400' },
                    { label: 'Invocations', value: analytics.invocation_count, color: 'text-[var(--brand-primary)]' },
                    { label: 'Success Rate', value: analytics.success_rate != null ? `${analytics.success_rate}%` : 'N/A', color: analytics.success_rate >= 90 ? 'text-emerald-400' : analytics.success_rate >= 70 ? 'text-amber-400' : 'text-[var(--text-muted)]' },
                  ].map(s => (
                    <div key={s.label} className="p-2 bg-[var(--surface-2)] rounded-lg text-center">
                      <p className={`text-sm font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-[9px] text-[var(--text-muted)]">{s.label}</p>
                    </div>
                  ))}
                </div>
                {analytics.last_used_at && (
                  <p className="text-[10px] text-[var(--text-muted)] mt-2">Last active: {new Date(analytics.last_used_at).toLocaleString()}</p>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="pt-4 border-t border-[var(--border-default)]">
              <div className="flex items-center justify-between text-[10px] text-[var(--text-muted)]">
                <span>ID: {detail.app_id || detail.submission_id || detail.provider_id}</span>
                <span>Source: {detail.source || detail.store_section}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Section Header ───────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, title, count, badge }) => (
  <div className="flex items-center gap-2 mb-4">
    <Icon size={18} weight="duotone" className="text-[var(--brand-primary)]" />
    <h2 className="text-base font-semibold text-[var(--text-primary)]">{title}</h2>
    <span className="px-1.5 py-0.5 bg-[var(--surface-3)] rounded text-[10px] text-[var(--text-muted)] font-medium">{count}</span>
    {badge && <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${badge.className}`}>{badge.label}</span>}
  </div>
);

// ─── Main App Store Page ───────────────────────────────────────────
const AppStore = () => {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [addToAgentApp, setAddToAgentApp] = useState(null);
  const [successToast, setSuccessToast] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [appsRes, installRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/app-store/apps${search ? `?search=${encodeURIComponent(search)}` : ''}`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/app-store/installed`, { credentials: 'include' }),
      ]);
      if (appsRes.ok) {
        const data = await appsRes.json();
        setApps(data.apps || []);
      }
      if (installRes.ok) {
        const data = await installRes.json();
        setInstallations(data.installations || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const installedAppIds = new Set(installations.filter(i => i.status === 'active').map(i => i.app_id));
  const getInstallation = (appId) => installations.find(i => i.app_id === appId && i.status === 'active');

  const nativeApps = apps.filter(a => a.store_section === 'native');
  const partnerApps = apps.filter(a => a.store_section === 'partner');
  const installedApps = apps.filter(a => installedAppIds.has(a.app_id || a.submission_id));

  const filteredNative = filterSource === 'partner' ? [] : nativeApps;
  const filteredPartner = filterSource === 'native' ? [] : partnerApps;

  const handleInstall = async (app) => {
    const appId = app.app_id || app.submission_id;
    try {
      const res = await fetch(`${BACKEND_URL}/api/app-store/apps/${appId}/install`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credentials: {} }),
      });
      if (res.ok) { fetchData(); setSelectedApp(null); }
    } catch (e) { console.error(e); }
  };

  const handleUninstall = async (app) => {
    const appId = app.app_id || app.submission_id;
    try {
      const res = await fetch(`${BACKEND_URL}/api/app-store/apps/${appId}/uninstall`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) { fetchData(); setSelectedApp(null); }
    } catch (e) { console.error(e); }
  };

  const handleToggle = async (app, enabled) => {
    const appId = app.app_id || app.submission_id;
    try {
      const res = await fetch(`${BACKEND_URL}/api/app-store/apps/${appId}/toggle`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (res.ok) { fetchData(); }
    } catch (e) { console.error(e); }
  };

  const handleAddToAgent = useCallback((app) => {
    setSelectedApp(null); // Close detail modal if open
    setAddToAgentApp(app);
  }, []);

  const handleAddToAgentSuccess = useCallback((data) => {
    setAddToAgentApp(null);
    setSuccessToast(data.message);
    setTimeout(() => setSuccessToast(null), 4000);
  }, []);

  return (
    <div className="min-h-screen bg-[var(--surface-0)] p-6" data-testid="app-store-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)] flex items-center gap-2">
              <Storefront size={26} weight="duotone" className="text-[var(--brand-primary)]" />
              App Store
            </h1>
            <p className="text-sm text-[var(--text-muted)] mt-1">Native and partner apps that power your AI agents.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/my-app-submissions')}
              className="px-3 py-2 border border-[var(--border-default)] text-[var(--text-secondary)] rounded-lg text-sm hover:border-[var(--brand-primary)]/40 hover:text-[var(--text-primary)] transition-colors flex items-center gap-1.5"
              data-testid="my-app-submissions-btn"
            >
              <Package size={16} />My Apps
            </button>
            {/* Developer Portal hidden — may resurface in future */}
            <button
              onClick={() => navigate('/submit-app')}
              className="px-3 py-2 bg-[var(--brand-primary)] text-white rounded-lg text-sm font-medium hover:bg-[var(--brand-primary-hover)] transition-colors flex items-center gap-1.5"
              data-testid="submit-app-btn"
            >
              <Plus size={16} weight="bold" />Submit App
            </button>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              type="text"
              placeholder="Search apps..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-[var(--surface-1)] border border-[var(--border-default)] rounded-lg text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--brand-primary)]"
              data-testid="app-search-input"
            />
          </div>
          <div className="flex items-center gap-1 bg-[var(--surface-1)] border border-[var(--border-default)] rounded-lg p-0.5">
            {[['all', 'All'], ['native', 'Native'], ['partner', 'Partner']].map(([val, label]) => (
              <button
                key={val}
                onClick={() => setFilterSource(val)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filterSource === val ? 'bg-[var(--brand-primary)] text-white' : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'}`}
                data-testid={`filter-${val}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><CircleNotch size={28} className="animate-spin text-[var(--brand-primary)]" /></div>
        ) : (
          <div className="space-y-8">
            {/* Installed Apps */}
            {installedApps.length > 0 && filterSource === 'all' && (
              <section data-testid="installed-apps-section">
                <SectionHeader icon={Download} title="Installed Apps" count={installedApps.length} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {installedApps.map(app => (
                    <AppCard
                      key={`inst-${app.app_id || app.submission_id}`}
                      app={app}
                      installed
                      onView={setSelectedApp}
                      onInstall={handleInstall}
                      onAddToAgent={handleAddToAgent}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Native Apps */}
            {filteredNative.length > 0 && (
              <section data-testid="native-apps-section">
                <SectionHeader icon={Cpu} title="Native Apps" count={filteredNative.length} badge={SECTION_BADGES.native} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredNative.map(app => (
                    <AppCard
                      key={app.app_id}
                      app={app}
                      installed={installedAppIds.has(app.app_id)}
                      onView={setSelectedApp}
                      onInstall={handleInstall}
                      onAddToAgent={handleAddToAgent}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Partner Apps */}
            {filteredPartner.length > 0 && (
              <section data-testid="partner-apps-section">
                <SectionHeader icon={PuzzlePiece} title="Partner Apps" count={filteredPartner.length} badge={SECTION_BADGES.partner} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredPartner.map(app => (
                    <AppCard
                      key={app.app_id || app.submission_id}
                      app={app}
                      installed={installedAppIds.has(app.app_id || app.submission_id)}
                      onView={setSelectedApp}
                      onInstall={handleInstall}
                      onAddToAgent={handleAddToAgent}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Empty State */}
            {filteredNative.length === 0 && filteredPartner.length === 0 && (
              <div className="text-center py-16 bg-[var(--surface-1)] rounded-xl border border-[var(--border-default)]" data-testid="no-apps-found">
                <Storefront size={40} className="mx-auto text-[var(--text-muted)] mb-3" />
                <p className="text-[var(--text-secondary)] text-sm">No apps found{search ? ` for "${search}"` : ''}</p>
                <p className="text-[var(--text-muted)] text-xs mt-1">Try a different search or filter</p>
              </div>
            )}
          </div>
        )}

        {/* Detail Modal */}
        {selectedApp && (
          <AppDetailModal
            app={selectedApp}
            installation={getInstallation(selectedApp.app_id || selectedApp.submission_id)}
            onClose={() => setSelectedApp(null)}
            onInstall={handleInstall}
            onUninstall={handleUninstall}
            onToggle={handleToggle}
            onAddToAgent={handleAddToAgent}
          />
        )}

        {/* Add to Agent Modal */}
        {addToAgentApp && (
          <AddToAgentModal
            capability={addToAgentApp}
            onClose={() => setAddToAgentApp(null)}
            onSuccess={handleAddToAgentSuccess}
          />
        )}

        {/* Success Toast */}
        {successToast && (
          <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-4 py-3 bg-emerald-500/15 border border-emerald-500/30 rounded-xl backdrop-blur-sm shadow-lg" data-testid="success-toast">
            <CheckCircle size={18} weight="fill" className="text-emerald-400" />
            <span className="text-sm text-emerald-300 font-medium">{successToast}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppStore;
